import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container } from "react-bootstrap";
import { useDispatch } from "react-redux";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import HomeHeader from "../common-components/HomeHeader";
import HomeFooter from "../common-components/HomeFooter";
import DataFetchStatus from "../common/DataFetchStatus";
import { getCmsList } from "../actions/cms";
import { findCmsEntryBySlug } from "../../utility/cmsContent";
import "./infoPage.css";

const safeParse = (value) => parse(DOMPurify.sanitize(value || ""));

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export default function InfoPage() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const [cmsList, setCmsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const { status, result, message } = await getCmsList(dispatch);
        if (cancelled) return;

        if (status === true && Array.isArray(result)) {
          setCmsList(result);
        } else {
          setCmsList([]);
          setFetchError(message || "Unable to load page content.");
        }
      } catch (err) {
        if (!cancelled) {
          setCmsList([]);
          setFetchError("Unable to load page content. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, dispatch]);

  const cmsEntry = useMemo(
    () => findCmsEntryBySlug(cmsList, slug),
    [cmsList, slug]
  );

  const pageTitle = cmsEntry?.title || "Information";
  const hasCmsContent = Boolean(stripHtml(cmsEntry?.content));
  const showNotFound = !loading && !fetchError && !cmsEntry;
  const showEmptyContent = !loading && !fetchError && cmsEntry && !hasCmsContent;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  return (
    <section className="info-page">
      <HomeHeader />
      <div className="info-page__main hm-page-offset">
        <div className="info-page__hero">
          <Container className="info-page__container">
            <p className="info-page__breadcrumb mb-3">
              <Link to="/">Home</Link>
              <span aria-hidden="true"> / </span>
              <Link to="/straight-bet">Betting</Link>
              <span aria-hidden="true"> / </span>
              <span>{pageTitle}</span>
            </p>
            <h1 className="info-page__hero-title">{pageTitle}</h1>
          </Container>
        </div>

        <Container className="info-page__container info-page__body py-4 pb-5">
          <DataFetchStatus loading={loading} error={fetchError}>
            {showNotFound ? (
              <div className="info-page__empty text-center py-5">
                <div className="info-page__empty-card mx-auto">
                  <h2 className="fs-4 mb-3">Page not found</h2>
                  <p className="mb-4">
                    This page is not set up yet. Browse live lines or contact
                    support for help.
                  </p>
                  <Link to="/straight-bet" className="btn btn-primary">
                    Go to betting
                  </Link>
                </div>
              </div>
            ) : null}

            {showEmptyContent ? (
              <div className="info-page__empty text-center py-5">
                <div className="info-page__empty-card mx-auto">
                  <h2 className="fs-4 mb-3">{pageTitle}</h2>
                  <p className="mb-4">
                    Content for this page has not been published yet. Please
                    check back soon.
                  </p>
                  <Link to="/straight-bet" className="btn btn-primary">
                    Go to betting
                  </Link>
                </div>
              </div>
            ) : null}

            {!loading && hasCmsContent ? (
              <article className="info-page__article">
                <div className="info-page__content">
                  {safeParse(cmsEntry.content)}
                </div>
                <div className="info-page__cta">
                  <p className="mb-3">Ready to place a bet?</p>
                  <Link to="/straight-bet" className="btn btn-primary">
                    Open sportsbook
                  </Link>
                </div>
              </article>
            ) : null}
          </DataFetchStatus>
        </Container>
      </div>
      <HomeFooter />
    </section>
  );
}
